import { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Plus, Edit, Trash2, Eye, EyeOff, MoreVertical, Search } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import { toast } from 'sonner';
import { adminApi } from '../../lib/api';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Helper to get full image URL
const getImageUrl = (url) => {
  if (!url) return 'https://images.unsplash.com/photo-1580541631950-7282082b53ce?w=100';
  if (url.startsWith('http')) return url;
  return `${BACKEND_URL}${url}`;
};

export default function AdminTours() {
  const [tours, setTours] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [deleteDialog, setDeleteDialog] = useState({ open: false, tour: null });

  const fetchTours = useCallback(async () => {
    try {
      const response = await adminApi.getTours();
      setTours(response.data);
    } catch (error) {
      toast.error('Error al cargar tours');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTours();
  }, [fetchTours]);

  const handleTogglePublish = async (tour) => {
    try {
      await adminApi.updateTour(tour.id, { is_published: !tour.is_published });
      toast.success(tour.is_published ? 'Tour despublicado' : 'Tour publicado');
      fetchTours();
    } catch (error) {
      toast.error('Error al actualizar tour');
    }
  };

  const handleDelete = async () => {
    if (!deleteDialog.tour) return;
    
    try {
      await adminApi.deleteTour(deleteDialog.tour.id);
      toast.success('Tour eliminado');
      setDeleteDialog({ open: false, tour: null });
      fetchTours();
    } catch (error) {
      toast.error('Error al eliminar tour');
    }
  };

  const filteredTours = tours.filter(tour =>
    tour.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    tour.category.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div data-testid="admin-tours">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl md:text-3xl font-serif text-stone-900">Tours</h1>
          <p className="text-stone-500 mt-1">Gestiona tus excursiones</p>
        </div>
        <Link to="/admin/tours/new">
          <Button className="rounded-xl gap-2" data-testid="admin-new-tour-btn">
            <Plus className="w-4 h-4" />
            Nuevo Tour
          </Button>
        </Link>
      </div>

      {/* Search */}
      <div className="mb-6">
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-stone-400" />
          <Input
            placeholder="Buscar tour..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 h-11 rounded-xl"
            data-testid="admin-tour-search"
          />
        </div>
      </div>

      {/* Tours List */}
      <div className="bg-white rounded-2xl border border-stone-200 overflow-hidden">
        {loading ? (
          <div className="p-8 space-y-4">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="h-20 bg-stone-100 rounded-xl skeleton" />
            ))}
          </div>
        ) : filteredTours.length === 0 ? (
          <div className="p-12 text-center">
            <p className="text-stone-500 mb-4">
              {searchTerm ? 'No se encontraron tours' : 'No hay tours creados'}
            </p>
            {!searchTerm && (
              <Link to="/admin/tours/new">
                <Button>Crear primer tour</Button>
              </Link>
            )}
          </div>
        ) : (
          <div className="divide-y divide-stone-100">
            {filteredTours.map((tour) => (
              <div 
                key={tour.id} 
                className="flex items-center gap-4 p-4 hover:bg-stone-50 transition-colors admin-table-row"
              >
                {/* Image */}
                <img
                  src={getImageUrl(tour.images?.[0])}
                  alt={tour.name}
                  className="w-20 h-20 rounded-xl object-cover flex-shrink-0"
                />
                
                {/* Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="font-semibold text-stone-900 truncate">{tour.name}</h3>
                    {tour.tags?.includes('Más vendido') && (
                      <Badge className="tag-popular text-xs">Más vendido</Badge>
                    )}
                  </div>
                  <div className="flex flex-wrap items-center gap-2 text-sm text-stone-500">
                    <span>${tour.price}</span>
                    <span>•</span>
                    <span>{tour.duration}</span>
                    <span>•</span>
                    <span>{tour.category}</span>
                  </div>
                </div>

                {/* Status */}
                <div className={`hidden sm:block px-3 py-1 rounded-full text-xs font-medium ${
                  tour.is_published 
                    ? 'bg-green-100 text-green-600' 
                    : 'bg-stone-100 text-stone-500'
                }`}>
                  {tour.is_published ? 'Publicado' : 'Borrador'}
                </div>

                {/* Actions */}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="icon" className="flex-shrink-0">
                      <MoreVertical className="w-4 h-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem asChild>
                      <Link to={`/admin/tours/${tour.id}/edit`} className="flex items-center">
                        <Edit className="w-4 h-4 mr-2" />
                        Editar
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => handleTogglePublish(tour)}>
                      {tour.is_published ? (
                        <>
                          <EyeOff className="w-4 h-4 mr-2" />
                          Despublicar
                        </>
                      ) : (
                        <>
                          <Eye className="w-4 h-4 mr-2" />
                          Publicar
                        </>
                      )}
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild>
                      <a href={`/tour/${tour.slug}`} target="_blank" rel="noopener noreferrer">
                        <Eye className="w-4 h-4 mr-2" />
                        Ver en web
                      </a>
                    </DropdownMenuItem>
                    <DropdownMenuItem 
                      className="text-red-600"
                      onClick={() => setDeleteDialog({ open: true, tour })}
                    >
                      <Trash2 className="w-4 h-4 mr-2" />
                      Eliminar
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Delete Dialog */}
      <AlertDialog open={deleteDialog.open} onOpenChange={(open) => setDeleteDialog({ open, tour: null })}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar este tour?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta acción no se puede deshacer. El tour "{deleteDialog.tour?.name}" 
              será eliminado permanentemente.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700"
            >
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
